<template>
  <div class="page">
    <VBreadcrumbs :items="breadcrumbs" class="mt-2" />
    <div class="heading">
      <h1 class="inline">{{ routine.name }}</h1>
      <VButton class="purple--text" type="text" @click="openRoutineDialog()">
        <VIcon class="left" icon="edit" />
        <span>Edit</span>
      </VButton>
    </div>
    <div>
      <span v-if="!!routine.description">{{ routine.description }}</span>
      <br v-if="!!routine.description" />
      <div v-if="routineUrl" class="link">
        <VIcon class="left" icon="link" />
        <a :href="routineUrl">
          {{ routineUrl }}
        </a>
      </div>
    </div>
    <div class="heading">
      <h2 class="inline">Routine History</h2>
      <VSpacer />
      <VButton class="purple white--text" @click="openPostDialog">
        <VIcon class="left" icon="add" />
        <span>New Entry</span>
      </VButton>
    </div>
    <PostCard
      v-for="(post, i) in posts"
      :key="i"
      :post="post"
      @delete-post="onDeletePost"
      @edit-post="openUpdatePostDialog(post)"
    />
    <VModal v-model="routineDialogOpen">
      <RoutineForm
        :workspace-id="activeWorkspaceId"
        :routine="routine"
        @archive="onArchiveRoutine()"
        @delete="onDeleteRoutine()"
        @save="onSaveRoutine()"
        @cancel="dismissDialog()"
      />
    </VModal>
    <VModal v-model="postDialogOpen">
      <NewPostForm :routine-id="id" @save="onSavePost()" />
    </VModal>
    <VModal v-model="updatePostDialogOpen">
      <EditPostForm :post="postToBeUpdated" @update="onUpdatePost()" />
    </VModal>
  </div>
</template>

<script setup lang="ts">
import { DateTime } from "luxon";
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import PostCard from "../components/PostCard.vue";
import NewPostForm from "../components/NewPostForm.vue";
import EditPostForm from "../components/EditPostForm.vue";
import NullableField from "../components/NullableField.vue";
import RoutineApi from "../api/routine";
import RoutineForm from "../components/RoutineForm.vue";
import { sanitizeUrl } from "@braintree/sanitize-url";
import User from "../models/user";
import { useWorkspace } from "@/stores/workspace";
import { useToast } from "vue-toast-notification";
import VBreadcrumbs from "../components/VBreadcrumbs.vue";
import VButton from "../components/VButton.vue";
import VIcon from "../components/VIcon.vue";
import VModal from "../components/VModal.vue";
import VSpacer from "../components/VSpacer.vue";
import VTable from "../components/VTable.vue";
import VTextArea from "../components/VTextArea.vue";
import "vue-toast-notification/dist/theme-default.css";
import PostApi from "../api/post";

const router = useRouter();
const route = useRoute();
const workspaceStore = useWorkspace();
const toast = useToast();

const props = defineProps<{
  id: string;
  slug: string;
}>();

// Reactive state
const limit = ref(10);
const offset = ref(0);
const postDialogOpen = ref(false);
const updatePostDialogOpen = ref(false);
const postToBeUpdated = ref({ text: "" });
const routineDialogOpen = ref(false);
const sortBy = ref([]);
const sortDir = ref([]);
const posts = ref([]);
const routine = ref({});
const q = ref("");

// Computed properties
const breadcrumbs = computed(() => [
  { to: `/${props.slug}/routines`, value: "Routines" },
  { value: props.id },
]);

const activeWorkspaceId = computed(() => {
  let workspace = workspaceStore.getWorkspaceBySlug(props.slug);
  return !!workspace && !!workspace.id ? workspace.id : "";
});

const routineUrl = computed(() => {
  if (!routine.value || !routine.value.url) {
    return "";
  }
  return sanitizeUrl(routine.value.url);
});

// Methods
const datetimeString = (datetime: string): string => {
  return DateTime.fromISO(datetime, { zone: "utc" })
    .toLocal()
    .toLocaleString(DateTime.DATETIME_MED);
};

const dismissDialog = () => {
  routineDialogOpen.value = false;
};

const fetchRoutine = async () => {
  let response = await RoutineApi.get(props.id);
  routine.value = response.data.routine;
};

const fetchPosts = async () => {
  let response = await PostApi.all({ routineId: props.id });
  posts.value = response.data.posts;
};

const openPostDialog = () => {
  postDialogOpen.value = true;
};

const openUpdatePostDialog = (post) => {
  postToBeUpdated.value = post;
  updatePostDialogOpen.value = true;
};

const openRoutineDialog = () => {
  routineDialogOpen.value = true;
};

const onArchiveRoutine = () => {
  toast.success("Routine archived.", { position: "bottom" });
  router.push(`/${props.slug}/routines`);
};

const onDeleteRoutine = () => {
  toast.success("Routine deleted.", { position: "bottom" });
  router.push(`/${props.slug}/routines`);
};

const onDeletePost = () => {
  toast.success("Entry deleted", { position: "bottom" });
  fetchPosts();
};

const onSavePost = () => {
  toast.success("Routine wardened.", { position: "bottom" });
  postDialogOpen.value = false;
  fetchPosts();
};

const onUpdatePost = () => {
  toast.success("Routine wardened.", { position: "bottom" });
  updatePostDialogOpen.value = false;
  fetchPosts();
};

const onSaveRoutine = () => {
  toast.success("Routine updated.", { position: "bottom" });
  routineDialogOpen.value = false;
  fetchRoutine();
};

// Lifecycle hooks
onMounted(() => {
  fetchRoutine();
  fetchPosts();
});
</script>

<style lang="scss" scoped>
.link {
  display: inline-flex;
}

.new-post {
  min-width: 600px;
}
</style>
